/**
 * One-shot decode for an app's title as the app opens (adapted from the
 * design bank's scramble index rows, but tied to the open, not to scroll):
 * characters cycle through glyphs and resolve left to right, once.
 *
 * The heading keeps its real text as its accessible name throughout, the
 * settled text is what renders without JS, and reduced motion skips it.
 */

const GLYPHS = 'ABCDEFGHJKLMNPRSTUVXYZ0123456789';
const glyph = () => GLYPHS.charAt(Math.floor(Math.random() * GLYPHS.length));

const running = new WeakMap<HTMLElement, () => void>();

export function decodeTitle(el: HTMLElement | null | undefined, duration = 420): void {
  if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  running.get(el)?.();
  // The leading text node only: the cyan stop and other markup stay put.
  const node = Array.from(el.childNodes).find((n): n is Text => n instanceof Text && !!n.data.trim());
  if (!node) return;
  const text = node.data;
  const hadLabel = el.hasAttribute('aria-label');
  if (!hadLabel) el.setAttribute('aria-label', text.trim());

  let frame = 0;
  const start = performance.now();
  const settle = () => {
    cancelAnimationFrame(frame);
    node.data = text;
    if (!hadLabel) el.removeAttribute('aria-label');
    running.delete(el);
  };
  const tick = (now: number) => {
    const t = Math.min(1, (now - start) / duration);
    const n = Math.floor(t * text.length);
    node.data = text.slice(0, n) + text.slice(n).replace(/\S/g, glyph);
    if (t < 1) frame = requestAnimationFrame(tick);
    else settle();
  };
  running.set(el, settle);
  frame = requestAnimationFrame(tick);
}
